import { array, object, nativeEnum, string, number, literal, boolean } from 'zod';

import { CustomDataSchema } from '@occc/validation';
import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import { Offer_Presentation_Priority, Offer_Presentation_Type } from '@occc/graphql';
import { IS_URL, isStringLengthUpTo } from '@occc-app/lib/constants/validationMessages';

import { INVALID_MARKUP_ERROR, RECEIPT_LABEL_ALPHANUMERIC_ERROR } from './messages';

const SHORT_TEXT_MAX_LENGTH = 255;
const LONG_TEXT_MAX_LENGTH = 4000;

export const RECEIPT_SHORT_DESCRIPTION_MAX_LENGTH = 8;
export const RECEIPT_LONG_DESCRIPTION_MAX_LENGTH = 24;
const RECEIPT_LABEL_ALPHANUMERIC_REGEX = /^[A-Za-z0-9 ]*$/;

// Matches fields inside `{{ }}`, e.g., `{{ membership.phone_number }}`.
// - `{{` and `}}` ensure the match starts and ends with double curly braces.
// - `\s*` allows optional spaces inside the braces.
// - `([^}]+)` captures the content inside, ensuring it excludes `}`.
// - `/g` flag ensures it finds **all occurrences** in the string.
const MARKUP_FIELD_REGEX = /{{\s*([^}]+)\s*}}/g;

const VALID_MARKUP_FIELDS = ['membership.phone_number', 'membership.id'];

export const validateMarkup = (value: string | null | undefined) => {
  if (!value) {
    return true;
  }

  const matches = [...value.matchAll(MARKUP_FIELD_REGEX)].map((match) => match[1].trim());

  return matches.every((match) => VALID_MARKUP_FIELDS.includes(match));
};

export const receiptContentSchema = object({
  shortDescription: string()
    .max(RECEIPT_SHORT_DESCRIPTION_MAX_LENGTH, {
      message: isStringLengthUpTo(RECEIPT_SHORT_DESCRIPTION_MAX_LENGTH),
    })
    .regex(RECEIPT_LABEL_ALPHANUMERIC_REGEX, { message: RECEIPT_LABEL_ALPHANUMERIC_ERROR })
    .optional(),
  longDescription: string()
    .max(RECEIPT_LONG_DESCRIPTION_MAX_LENGTH, {
      message: isStringLengthUpTo(RECEIPT_LONG_DESCRIPTION_MAX_LENGTH),
    })
    .regex(RECEIPT_LABEL_ALPHANUMERIC_REGEX, { message: RECEIPT_LABEL_ALPHANUMERIC_ERROR })
    .optional(),
});

export const marketingContentSchema = object({
  title: string()
    .max(SHORT_TEXT_MAX_LENGTH, { message: isStringLengthUpTo(SHORT_TEXT_MAX_LENGTH) })
    .optional(),
  shortDescription: string()
    .max(SHORT_TEXT_MAX_LENGTH, { message: isStringLengthUpTo(SHORT_TEXT_MAX_LENGTH) })
    .optional()
    .refine(validateMarkup, {
      message: INVALID_MARKUP_ERROR,
    }),
  longDescription: string()
    .max(LONG_TEXT_MAX_LENGTH, { message: isStringLengthUpTo(LONG_TEXT_MAX_LENGTH) })
    .optional()
    .refine(validateMarkup, {
      message: INVALID_MARKUP_ERROR,
    }),
  imageUrl: string()
    .url({ message: IS_URL })
    .max(SHORT_TEXT_MAX_LENGTH, { message: isStringLengthUpTo(SHORT_TEXT_MAX_LENGTH) })
    .optional()
    .refine(validateMarkup, {
      message: INVALID_MARKUP_ERROR,
    }),
  terms: string()
    .max(LONG_TEXT_MAX_LENGTH, { message: isStringLengthUpTo(LONG_TEXT_MAX_LENGTH) })
    .optional()
    .refine(validateMarkup, {
      message: INVALID_MARKUP_ERROR,
    }),
  termsUrl: string()
    .url({ message: IS_URL })
    .max(SHORT_TEXT_MAX_LENGTH, { message: isStringLengthUpTo(SHORT_TEXT_MAX_LENGTH) })
    .optional()
    .refine(validateMarkup, {
      message: INVALID_MARKUP_ERROR,
    }),
  disclaimer: string()
    .max(LONG_TEXT_MAX_LENGTH, { message: isStringLengthUpTo(LONG_TEXT_MAX_LENGTH) })
    .optional()
    .refine(validateMarkup, {
      message: INVALID_MARKUP_ERROR,
    }),
  disclaimerUrl: string()
    .url({ message: IS_URL })
    .max(SHORT_TEXT_MAX_LENGTH, { message: isStringLengthUpTo(SHORT_TEXT_MAX_LENGTH) })
    .optional()
    .refine(validateMarkup, {
      message: INVALID_MARKUP_ERROR,
    }),
  externalUrl: string().url({ message: IS_URL }).optional().refine(validateMarkup, {
    message: INVALID_MARKUP_ERROR,
  }),
  surgeonGeneralsWarning: string()
    .max(SHORT_TEXT_MAX_LENGTH, { message: isStringLengthUpTo(SHORT_TEXT_MAX_LENGTH) })
    .optional()
    .refine(validateMarkup, {
      message: INVALID_MARKUP_ERROR,
    }),
});

export const offerTrackingSchema = object({
  presentationType: nativeEnum(Offer_Presentation_Type).optional(),
  presentationPriority: nativeEnum(Offer_Presentation_Priority).optional(),
  displayForOrderAhead: boolean().optional(),
  fundingSourceName: string().nullable().default(null),
  trackingId: string().trim().nullable().optional(),
  includeActivityLogsInAltriaDDM: boolean().nullable().optional(),
}).superRefine((data, ctx) => {
  if (data.includeActivityLogsInAltriaDDM && !data.trackingId) {
    ctx.addIssue({
      code: 'custom',
      message: 'Tracking ID is required when DDM tracking is enabled',
      path: ['trackingId'],
    });
  }
});

const designFormSchema = object({
  offer: offerTrackingSchema,
  marketingContent: marketingContentSchema.default({}),
  receiptContent: receiptContentSchema.optional(),
  displayCategories: array(
    object({
      id: string(),
      name: string(),
      rank: number(),
      __typename: literal('DisplayCategory'),
    }),
  ),
  customData: CustomDataSchema,
});

export const validateDesignForm = makeValidate(designFormSchema);
